import { useState, useEffect } from 'react';
import {
  Typography, Box, Button, Grid, Card, CardContent,
  InputAdornment, TextField, Tooltip, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tabs, Tab, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import BlockIcon from '@mui/icons-material/Block';
import SearchIcon from '@mui/icons-material/Search';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import InfoIcon from '@mui/icons-material/Info';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import WarningIcon from '@mui/icons-material/Warning';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { RootState, useAppDispatch, useAppSelector } from "../../../../state/state.tsx";
import { openModal } from "../../../../state/ModalSlice/ModalSlice.ts";
import { selectEffectiveUID } from "../../../../state/UserStatesV2/userSliceV2.ts";
import AddLeadModal from '../../../Common/Modals/Inflow/AddLeadModal.tsx';
import EditLeadModal from './Components/EditLeadModal';
import LeadsBoard from './Components/LeadsBoard';
import DragDropWrapper from './Components/DragDropWrapper';
import GenericBoard from './Components/GenericBoard';
import AddBoardModal from './Components/AddBoardModal';
import AddColumnModal from './Components/AddColumnModal';
import AddItemModal from './Components/AddItemModal';
import { toast } from 'react-toastify';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { DropResult } from '@hello-pangea/dnd';
import { db } from "../../../../Config/firebase.ts";
import { PageName } from "../../../../enums.ts";
import { collection, doc, updateDoc, deleteDoc, onSnapshot, query, orderBy, getDocs } from "firebase/firestore";
// Import the enhanced drag and drop styles
import './Components/draggable.css';
import AccessControl from '../../Components/AccessControl.tsx';

export interface Lead {
  id: string;
  name: string;
  phone: string;
  email: string;
  source: string;
  lastContact: string;
  notes: string;
  status: 'hot' | 'cold' | 'obsolete' | 'win';
  originatorId?: string;
  createdAt?: number;
  updatedAt?: number;
  createdBy?: string;
  notesHistory?: Array<{
    timestamp: number;
    note: string;
    updatedBy?: string;
  }>;
  updateHistory?: Array<{
    timestamp: number;
    updatedBy?: string;
    changes: Record<string, any>;
  }>;
}

export default function Inflow() {
  const dispatch = useAppDispatch();
  const [searchQuery, setSearchQuery] = useState('');
  const [leads, setLeads] = useState<Lead[]>([]);
  const [currentLead, setCurrentLead] = useState<Lead | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState<string>('');
  const [tab, setTab] = useState<'leads' | 'boards'>('leads');
  type Board = { id: string; name: string; createdAt?: number };
  const [boards, setBoards] = useState<Board[]>([]);
  const [selectedBoardId, setSelectedBoardId] = useState<string | null>(null);
  const [showDeleteBoardConfirm, setShowDeleteBoardConfirm] = useState(false);
  const [boardToDelete, setBoardToDelete] = useState<string | null>(null);
  const userId = useAppSelector(selectEffectiveUID);
  const userEmail = useAppSelector((state: RootState) => state.userSliceV2.data?.email);
  const userDisplayName = useAppSelector((state: RootState) => state.userSliceV2.data?.display_name);

  // Filter leads based on search query
  const filteredLeads = leads.filter(lead =>
    lead.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lead.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lead.phone?.includes(searchQuery) ||
    lead.notes?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const hotLeads = filteredLeads.filter(lead => lead.status === 'hot');
  const coldLeads = filteredLeads.filter(lead => lead.status === 'cold');
  const obsoleteLeads = filteredLeads.filter(lead => lead.status === 'obsolete');
  const winLeads = filteredLeads.filter(lead => lead.status === 'win');

  // Fetch leads from Firestore
  useEffect(() => {
    if (!userId) return;

    setIsLoading(true);

    // Create a query to get leads in descending order of creation time
    const leadsQuery = query(
      collection(db, `ServiceProviders/${userId}/Leads`),
      orderBy("createdAt", "desc")
    );

    // Set up real-time listener for leads
    const unsubscribe = onSnapshot(
      leadsQuery,
      (snapshot) => {
        const leadsData: Lead[] = [];
        snapshot.forEach((doc) => {
          leadsData.push({
            id: doc.id,
            ...doc.data() as Omit<Lead, 'id'>
          });
        });
        setLeads(leadsData);
        setIsLoading(false);
      },
      (error) => {
        console.error("Error fetching leads:", error);
        toast.error("Error loading leads. Please refresh the page.");
        setIsLoading(false);
      }
    );

    // Clean up the listener when component unmounts
    return () => unsubscribe();
  }, [userId]);

  // Fetch custom boards list
  useEffect(() => {
    if (!userId) return;

    const boardsQuery = query(
      collection(db, `ServiceProviders/${userId}/InflowBoards`),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(boardsQuery, (snapshot) => {
      const data: Board[] = [];
      snapshot.forEach((d) => data.push({ id: d.id, ...(d.data() as Omit<Board, 'id'>) }));
      setBoards(data);
      if (!selectedBoardId && data.length) {
        setSelectedBoardId(data[0].id);
      }
    });

    return () => unsubscribe();
  }, [userId]);

  const handleOpenAddLeadModal = () => {
    dispatch(openModal("AddLeadModalOpen"));
  };

  const handleOpenEditLeadModal = (lead: Lead) => {
    setCurrentLead(lead);
    dispatch(openModal("EditLeadModalOpen"));
  };

  const openDeleteDialog = (id: string) => {
    setLeadToDelete(id);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setLeadToDelete('');
  };

  const handleDeleteLead = async () => {
    if (!userId) {
      toast.error("User not authenticated");
      closeDeleteDialog();
      return;
    }

    try {
      // Delete the lead from Firestore
      const leadRef = doc(db, `ServiceProviders/${userId}/Leads`, leadToDelete);
      await deleteDoc(leadRef);
      toast.success('Lead deleted successfully!');
      closeDeleteDialog();
    } catch (error) {
      console.error("Error deleting lead:", error);
      toast.error('Error deleting lead. Please try again.');
      closeDeleteDialog();
    }
  };

  const moveLead = async (leadId: string, newStatus: 'hot' | 'cold' | 'obsolete' | 'win') => {
    if (!userId) {
      toast.error("User not authenticated");
      return;
    }

    try {
      const timestamp = Date.now();
      const updatedBy = userDisplayName || userEmail || 'Unknown';

      // Get the lead to update from the local state
      const leadToUpdate = leads.find(lead => lead.id === leadId);

      // If lead not found in local state, log it but continue with update
      if (!leadToUpdate) {
        console.warn(`Lead with ID ${leadId} not found in local state. Proceeding with update.`);
      }

      // Create update history entry for status change
      const updateHistoryEntry = {
        timestamp,
        updatedBy,
        changes: {
          status: {
            from: leadToUpdate?.status || 'unknown',
            to: newStatus
          }
        }
      };

      // Create the update data
      const updateData: any = {
        status: newStatus,
        updatedAt: timestamp
      };

      // Add update history
      if (leadToUpdate?.updateHistory) {
        updateData.updateHistory = [...leadToUpdate.updateHistory, updateHistoryEntry];
      } else {
        updateData.updateHistory = [updateHistoryEntry];
      }

      // Update in Firestore
      const leadRef = doc(db, `ServiceProviders/${userId}/Leads`, leadId);
      await updateDoc(leadRef, updateData);

      // Success message in console

    } catch (error) {
      console.error("Error moving lead:", error);
      toast.error("Error updating lead status. Please try again.");
    }
  };

  const deleteBoardDocument = async () => {
    try {
      // Delete the board document (this will cascade delete columns and items)
      const boardRef = doc(db, `ServiceProviders/${userId}/InflowBoards/${boardToDelete}`);
      await deleteDoc(boardRef);
      
      toast.success('Board deleted successfully');
      setShowDeleteBoardConfirm(false);
      setBoardToDelete(null);
      
      // Reset selection if deleted board was selected
      if (selectedBoardId === boardToDelete) {
        setSelectedBoardId(null);
      }
    } catch (error) {
      console.error('Error deleting board:', error);
      toast.error('Failed to delete board');
    }
  };

  const openDeleteBoardDialog = (boardId: string) => {
    setBoardToDelete(boardId);
    setShowDeleteBoardConfirm(true);
  };

  const handleDeleteBoard = async () => {
    // Only proceed if this is explicitly called from the delete button
    if (!userId || !boardToDelete || !showDeleteBoardConfirm) {
      return;
    }
    
    // Additional safety check - ensure we're in the right context
    if (tab !== 'boards') {
      return;
    }
    
    try {
      // Check if board has any cards before deleting
      const itemsQuery = query(
        collection(db, `ServiceProviders/${userId}/InflowBoards/${boardToDelete}/Items`)
      );
      
      const itemsSnapshot = await getDocs(itemsQuery);
      if (!itemsSnapshot.empty) {
        toast.error('Cannot delete board: It contains cards. Please delete all cards first.');
        setShowDeleteBoardConfirm(false);
        setBoardToDelete(null);
        return;
      }
      
      // If no cards, proceed with deletion
      await deleteBoardDocument();
      
    } catch (error) {
      console.error('Error checking board contents:', error);
      toast.error('Failed to check board contents');
      // Reset states on error
      setShowDeleteBoardConfirm(false);
      setBoardToDelete(null);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading leads...
        </Typography>
      </Box>
    );
  }

  return (
    <AccessControl pageName={PageName.Inflow} >
    <div className="container mx-auto px-4 py-8">
      <Typography variant="h4" component="h1" gutterBottom>
        Inflow
      </Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }} aria-label="Inflow tabs">
        <Tab label="Leads" value="leads" />
        <Tab label="Boards" value="boards" />
      </Tabs>

      {tab === 'leads' && (
      <>
      {/* Action Buttons */}
      <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'space-between' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<PersonAddIcon />}
          onClick={handleOpenAddLeadModal}
          sx={{
            borderRadius: '28px',
            px: 3,
            py: 1,
            boxShadow: '0 4px 10px rgba(25, 118, 210, 0.3)',
            background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 6px 15px rgba(25, 118, 210, 0.4)',
              transform: 'translateY(-2px)'
            }
          }}
        >
          Add New Lead
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Drag and drop cards between different status columns">
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'rgba(76, 175, 80, 0.1)',
              px: 2,
              py: 1,
              borderRadius: 2,
              mr: 1,
              border: '1px dashed rgba(76, 175, 80, 0.5)',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              animation: 'pulse 2s infinite ease-in-out',
              '@keyframes pulse': {
                '0%': {
                  boxShadow: '0 0 0 0 rgba(76, 175, 80, 0.4)',
                },
                '70%': {
                  boxShadow: '0 0 0 6px rgba(76, 175, 80, 0)',
                },
                '100%': {
                  boxShadow: '0 0 0 0 rgba(76, 175, 80, 0)',
                },
              }
            }}>
              <DragIndicatorIcon fontSize="small" sx={{ mr: 1, color: 'success.main' }} />
              <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'success.dark' }}>
                ⟵ Drag cards to move leads between columns ⟶
              </Typography>
            </Box>
          </Tooltip>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} className="mb-6">
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="50%"
                  bgcolor="rgba(230, 81, 0, 0.1)"
                  width={48}
                  height={48}
                  mr={2}
                >
                  <LocalFireDepartmentIcon sx={{ color: "orange" }} />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Hot Leads
                  </Typography>
                  <Typography variant="h6">
                    {hotLeads.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="50%"
                  bgcolor="rgba(3, 169, 244, 0.1)"
                  width={48}
                  height={48}
                  mr={2}
                >
                  <AcUnitIcon sx={{ color: "#03a9f4" }} />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Cold Leads
                  </Typography>
                  <Typography variant="h6">
                    {coldLeads.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="50%"
                  bgcolor="rgba(120, 120, 120, 0.1)"
                  width={48}
                  height={48}
                  mr={2}
                >
                  <BlockIcon color="action" />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Obsolete Leads
                  </Typography>
                  <Typography variant="h6">
                    {obsoleteLeads.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="50%"
                  bgcolor="rgba(76, 175, 80, 0.1)"
                  width={48}
                  height={48}
                  mr={2}
                >
                  <EmojiEventsIcon sx={{ color: "#ffc107" }} />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Wins
                  </Typography>
                  <Typography variant="h6">
                    {winLeads.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search Controls */}
      <Box sx={{ mb: 3 }}>
        <TextField
          label="Search Leads"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          style={{ width: '300px' }}
        />
      </Box>

      {/* Board View Only */}
      <Box sx={{ mb: 6 }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 2,
          p: 1,
          bgcolor: 'rgba(33, 150, 243, 0.08)',
          borderRadius: 1
        }}>
          <InfoIcon sx={{ color: 'info.main', mr: 1 }} />
          <Typography variant="body2">
            Tip: Click and drag a lead card to move it between columns. The lead status will update automatically.
          </Typography>
        </Box>
        <DragDropWrapper
          onDragEnd={(result: DropResult) => {

            // Only process if there's a valid destination
            if (result.destination) {
              // Get the new status from the destination droppableId
              const newStatus = result.destination.droppableId as 'hot' | 'cold' | 'obsolete' | 'win';

              // Call moveLead directly with the draggableId and the new status
              if (result.draggableId) {
                moveLead(result.draggableId, newStatus);
              }
            }
          }}
        >
          <LeadsBoard
            leads={filteredLeads}
            onEdit={handleOpenEditLeadModal}
            onDelete={openDeleteDialog}
            onMove={moveLead}
          />
        </DragDropWrapper>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '10px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            p: 2,
            bgcolor: 'error.light',
            color: 'error.contrastText',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="error" />
            <Typography variant="h6">Confirm Deletion</Typography>
          </Box>
          <IconButton
            edge="end"
            color="inherit"
            onClick={closeDeleteDialog}
            aria-label="close"
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 3 }}>
          <Typography variant="body1">
            Are you sure you want to delete this lead? This action cannot be undone.
          </Typography>
          <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ mt: 2 }}>
            All information associated with this lead will be permanently removed.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={closeDeleteDialog}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 'medium'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteLead}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            sx={{
              borderRadius: '8px',
              background: 'linear-gradient(45deg, #f44336 30%, #e57373 90%)',
              boxShadow: '0 2px 8px rgba(244, 67, 54, 0.3)',
              textTransform: 'none',
              fontWeight: 'medium',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(244, 67, 54, 0.4)',
              }
            }}
          >
            Delete Lead
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modals */}
      <AddLeadModal />
      {currentLead && <EditLeadModal lead={currentLead} />}
      </>)}

      {tab === 'boards' && (
        <>
          <Box sx={{ 
            mb: 3, 
            display: 'flex', 
            gap: 2, 
            alignItems: 'center', 
            flexWrap: 'wrap',
            p: 2,
            bgcolor: 'grey.50',
            borderRadius: 2,
            border: '1px solid #e0e0e0'
          }}>
            <Button 
              variant="contained" 
              onClick={() => dispatch(openModal('AddBoardModalOpen'))}
              sx={{ 
                borderRadius: '20px',
                textTransform: 'none',
                fontWeight: 500,
                px: 3
              }}
            >
              New Board
            </Button>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="boards-select-label">Select Board</InputLabel>
              <Select
                labelId="boards-select-label"
                label="Select Board"
                value={selectedBoardId || ''}
                onChange={(e) => setSelectedBoardId(e.target.value as string)}
                sx={{ borderRadius: '8px' }}
              >
                {boards.map((b) => (
                  <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button 
              variant="outlined" 
              disabled={!selectedBoardId} 
              onClick={() => dispatch(openModal('AddColumnModalOpen'))}
              sx={{ borderRadius: '20px', textTransform: 'none' }}
            >
              Add Column
            </Button>
            <Button 
              variant="outlined" 
              disabled={!selectedBoardId} 
              onClick={() => dispatch(openModal('AddItemModalOpen'))}
              sx={{ borderRadius: '20px', textTransform: 'none' }}
            >
              Add Card
            </Button>
            {selectedBoardId && (
              <Button 
                variant="outlined" 
                color="error" 
                onClick={() => openDeleteBoardDialog(selectedBoardId)}
                sx={{ 
                  ml: 'auto',
                  borderRadius: '20px',
                  textTransform: 'none',
                  borderColor: 'error.main',
                  color: 'error.main',
                  '&:hover': {
                    borderColor: 'error.dark',
                    backgroundColor: 'error.50'
                  }
                }}
              >
                Delete Board
              </Button>
            )}
          </Box>
          {selectedBoardId ? (
            <GenericBoard
              userId={userId as string}
              boardId={selectedBoardId}
              onRequestAddItem={() => dispatch(openModal('AddItemModalOpen'))}
            />
          ) : (
            <Box sx={{ 
              py: 8, 
              textAlign: 'center',
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: '2px dashed #ccc'
            }}>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                No Board Selected
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create a new board or select an existing one to get started.
              </Typography>
            </Box>
          )}
          {/* Boards Modals */}
          <AddBoardModal />
          <AddColumnModal boardId={selectedBoardId} />
          <AddItemModal boardId={selectedBoardId} />
        </>
      )}

      {/* Delete Board Confirmation Dialog */}
      <Dialog
        open={showDeleteBoardConfirm}
        onClose={() => setShowDeleteBoardConfirm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
          Confirm Board Deletion
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography>
            Are you sure you want to delete this board? This will permanently remove all columns and cards.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteBoardConfirm(false)}>Cancel</Button>
          <Button onClick={handleDeleteBoard} color="error" variant="contained">
            Delete Board
          </Button>
        </DialogActions>
      </Dialog>
    </div>
    </AccessControl>
  );
} 